/**
 * CrossFieldWarning — inline chip for a cross-field validation
 * warning (Configuration Upgrade CU3).
 *
 * The warnings module at ./crossFieldWarnings.ts already computes
 * `Array<{ field, severity, message, suggestedFix? }>` per per-type
 * prefs. Before CU3 the rerun wizard never rendered these — only the
 * New Report wizard surfaced them via the validation gate. This
 * component renders each warning as a yellow chip beneath the
 * relevant field with an optional one-click suggestedFix CTA.
 *
 * The wizard mounts `CrossFieldWarning` either:
 *   - inline (per-field) — via PrefsValidationProvider's warningsByField
 *     map, which FieldRow reads and renders as a sibling line, or
 *   - as a footer list (`CrossFieldWarningList`) — rendered once below
 *     the form for warnings whose `field` doesn't map to any visible
 *     FieldRow (e.g. KPI weights, latencyTargetsMs).
 *
 * Both paths use this primitive.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { CrossFieldWarning as CrossFieldWarningType } from './crossFieldWarnings';

export interface CrossFieldWarningChipProps {
  warning: CrossFieldWarningType;
  /** Called when the user clicks the suggested-fix button. */
  onApplyFix?: (fixField: string, value: unknown) => void;
}

export const CrossFieldWarningChip: React.FC<CrossFieldWarningChipProps> = ({
  warning,
  onApplyFix,
}) => {
  const isError = warning.severity === 'error';
  return (
    <div
      className={`flex items-start gap-2 type-body px-2 py-1.5 border ${
        isError
          ? 'text-danger border-danger/30 bg-danger/5'
          : 'text-warning border-warning/30 bg-warning/5'
      }`}
      role={isError ? 'alert' : 'status'}
      data-testid={`cross-field-warning-${warning.field}`}
    >
      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1 leading-snug">
        <div>{warning.message}</div>
        {warning.suggestedFix && onApplyFix && (
          <button
            type="button"
            onClick={() => onApplyFix(warning.suggestedFix!.fixField, warning.suggestedFix!.suggestedValue)}
            className="mt-1 inline-flex items-center type-meta uppercase tracking-widest underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
            data-testid={`cross-field-fix-${warning.suggestedFix.fixField}`}
          >
            {warning.suggestedFix.label}
          </button>
        )}
      </div>
    </div>
  );
};

export interface CrossFieldWarningListProps {
  warnings: CrossFieldWarningType[];
  onApplyFix?: (fixField: string, value: unknown) => void;
}

/** Footer list — renders one chip per warning. */
export const CrossFieldWarningList: React.FC<CrossFieldWarningListProps> = ({
  warnings,
  onApplyFix,
}) => {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid="cross-field-warnings-list">
      {warnings.map((w, i) => (
        <CrossFieldWarningChip key={`${w.field}-${i}`} warning={w} onApplyFix={onApplyFix} />
      ))}
    </div>
  );
};
